import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { createUser, ApiError } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { fullName } from '../../lib/format';

interface Props {
  open: boolean;
  onClose: () => void;
}

const inputStyle = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  ...T.ui,
} as const;

export function NewUserModal({ open, onClose }: Props) {
  const { push } = useToast();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('+995');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isCoach, setIsCoach] = useState(false);
  const [credit, setCredit] = useState('0');
  const [busy, setBusy] = useState(false);
  const phoneRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPhone('+995');
      setFirstName('');
      setLastName('');
      setIsCoach(false);
      setCredit('0');
      setBusy(false);
    }
  }, [open]);

  // Focus the phone field on open instead of using the autoFocus prop.
  useEffect(() => {
    if (open) {
      phoneRef.current?.focus();
    }
  }, [open]);

  const handleCreate = async () => {
    const trimmedPhone = phone.trim();
    const trimmedName = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedPhone.startsWith('+')) {
      push('error', "Phone must start with '+' (E.164, e.g. +995591234567).");
      return;
    }
    if (!trimmedName) {
      push('error', 'First name is required.');
      return;
    }
    if (!trimmedLast) {
      push('error', 'Last name is required.');
      return;
    }
    const creditMinutes = Number(credit);
    if (!Number.isInteger(creditMinutes) || creditMinutes < 0) {
      push('error', 'Starting credit must be 0 or a positive whole number.');
      return;
    }
    setBusy(true);
    try {
      const user = await createUser({
        phone: trimmedPhone,
        first_name: trimmedName,
        last_name: trimmedLast,
        is_coach: isCoach,
        credit_minutes: creditMinutes,
      });
      push('success', `Created ${fullName(user)}.`);
      onClose();
      navigate(`/users/${user.id}`);
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Failed to create user.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New user">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Phone (E.164)</span>
          <input
            ref={phoneRef}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+995591234567"
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>First name</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Last name</span>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={isCoach}
            onChange={(e) => setIsCoach(e.target.checked)}
          />
          <span style={T.label}>Mark as coach</span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Starting credit (minutes)</span>
          <input
            type="number"
            value={credit}
            min={0}
            step={30}
            onChange={(e) => setCredit(e.target.value)}
            style={inputStyle}
          />
        </label>
        <p style={{ ...T.caption, margin: 0 }}>
          Creates a real account from the phone number — no SMS is sent. The
          person can later sign in with this phone and land on the same account.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void handleCreate()} disabled={busy}>
            {busy ? 'Creating…' : 'Create user'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
